/* Canva, from the browser side. The server half (and why her sign-in lives in a
 * sealed cookie rather than in Supabase) is api/canva.js and api/_lib/canva.js.
 * Admin-only by her decision, 2026-10-01. */
import { useEffect } from "react";
import { useSession } from "@clerk/clerk-react";

export async function callCanva(session, body) {
  const token = await session?.getToken();
  const res = await fetch("/api/canva", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  return res.json().catch(() => ({ ok: false, error: `Brill's Canva connection answered ${res.status}.` }));
}

/** Canva's return to Brill's front page: ?code=…&state=canva_… — only a state
 *  Brill made starts with "canva_", so nothing else's ?code is mistaken for it. */
export function canvaReturnOf(search) {
  const q = new URLSearchParams(search || "");
  const code = q.get("code");
  const state = q.get("state") || "";
  const error = q.get("error");
  /* CANVA'S OWN WORDS. Without them every refusal read "Canva didn't send
     Brill back a sign-in" and nobody could tell a scope from a wrong account
     (2026-10-26, her production try). */
  const said = q.get("error_description") || "";
  if (!state.startsWith("canva_")) return null;
  return { code, state, error, said };
}

/** What to tell her when Canva came back without a sign-in. */
export function canvaRefusal({ error, said } = {}) {
  if (!error) return "Canva didn't send Brill back a sign-in.";
  return `Canva didn't connect. Canva said: ${said ? `${said} (${error})` : error}`;
}

export const CANVA_MESSAGE = "brill-canva";

/* KEPT THROUGH A SIGN-IN. Her report on the first try, 2026-10-01: "after you
   get into canva you have to sign back into brill". The sign-in screen can
   rewrite the address and take Canva's code with it, so the code is set aside
   the moment this file loads — before anything else runs — and read back once
   she is in. Canva's code lasts minutes; so does this (PENDING_AGE). */
const PENDING = "brill.canva.pending";
const PENDING_AGE = 10 * 60 * 1000;
try {
  const back = typeof window !== "undefined" && canvaReturnOf(window.location.search);
  if (back) window.sessionStorage.setItem(PENDING, JSON.stringify({ ...back, t: Date.now() }));
} catch { /* private window: the address is the only copy */ }

function pendingReturn() {
  const fromUrl = canvaReturnOf(window.location.search);
  if (fromUrl) return fromUrl;
  try {
    const p = JSON.parse(window.sessionStorage.getItem(PENDING) || "null");
    return p && Date.now() - p.t < PENDING_AGE ? p : null;
  } catch { return null; }
}

/* THE CONNECT WINDOW. Connect opens Canva's Allow screen in a small window so
   she never leaves the slide or post she was on; Canva sends that window back
   to Brill's front page, where this finishes the sign-in, tells the window that
   opened it, and closes. Without a window (a blocked pop-up), the same page was
   used, and it simply tidies the address and stays. */
/* ONCE PER RETURN. A Canva code works one time; React's development mode runs
   effects twice, and a second "connect" would fail and overwrite the first's
   good news with an error. */
let finishing = null;

export function useCanvaReturn() {
  const { session, isLoaded } = useSession();
  useEffect(() => {
    const back = pendingReturn();
    if (!back || !isLoaded || !session || finishing === back.state) return;
    finishing = back.state;
    try { window.sessionStorage.removeItem(PENDING); } catch { /* fine */ }
    (async () => {
      const r = back.error || !back.code
        ? { ok: false, error: back.error === "access_denied" ? "You didn't allow Brill in Canva, so nothing was connected." : canvaRefusal(back) }
        : await callCanva(session, { job: "connect", code: back.code, state: back.state });
      const tell = { type: CANVA_MESSAGE, ok: !!r.ok, name: r.name || "", error: r.ok ? "" : (r.error || "Canva didn't connect.") };
      if (window.opener && !window.opener.closed) {
        try { window.opener.postMessage(tell, window.location.origin); } catch { /* opener gone */ }
        window.close();
        return;
      }
      window.history.replaceState(null, "", window.location.pathname || "/");
      try { window.sessionStorage.setItem("brill.canva.result", JSON.stringify(tell)); } catch { /* private window */ }
    })();
  }, [isLoaded, session]);
}
